// 單款遊戲詳情：依 index 列的 hash 載入（hash 相同代表內容相同，可直接用快取）。
import type { GameDetail, IndexRow } from '@dayone/core/snapshot';
import { useCallback, useEffect, useState } from 'react';
import { LoadError, type SnapshotClient } from './client.ts';

export interface DetailState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  detail: GameDetail | null;
  error: LoadError | null;
  retry: () => void;
}

export function useGameDetail(row: IndexRow | null, client: SnapshotClient): DetailState {
  const [detail, setDetail] = useState<GameDetail | null>(null);
  const [error, setError] = useState<LoadError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const id = row?.id;
  const hash = row?.x;

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt 是重試的觸發器
  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!id || !hash) return;
    let alive = true;
    client.detail(id, hash).then(
      (d) => alive && setDetail(d),
      (e) => alive && setError(e instanceof LoadError ? e : new LoadError('unknown', String(e))),
    );
    return () => {
      alive = false;
    };
  }, [id, hash, client, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const status = !id ? 'idle' : detail ? 'ready' : error ? 'error' : 'loading';
  return { status, detail, error, retry };
}
